import { useGetCategories, useBaseDeleteAccount } from '@/api/react-query'
import { TenantLogo } from '@/components/tenant-logo'
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle
} from '@/components/ui/alert-dialog'
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle
} from '@/components/ui/dialog'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuLabel,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu'
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card'
import { Image } from '@/components/ui/image'
import { useGetCurrentOrder } from '@/contexts/order-context'
import { useI18n } from '@/i18n/i18n-react'
import { Locales } from '@/i18n/i18n-types'
import { allLocales } from '@/i18n/i18n-util'
import { tenant } from '@/tenants/tenant'
import { hasAccountLogin } from '@/utils/tester-mode'
import { useLogOut, useAuth } from '@/providers/auth-provider'
import { Link, useRouterState } from '@tanstack/react-router'
import { Check, CreditCard, Globe, LogOut, ReceiptText, ShoppingCart, Trash2, User } from 'lucide-react'
import { toast } from 'sonner'
import { useState } from 'react'
import { Button } from './ui/button'
import { ScrollArea, ScrollBar } from './ui/scroll-area'

const languages = [
	{ code: 'is', name: 'Íslenska', flag: '🇮🇸' },
	{ code: 'en', name: 'English', flag: '🇺🇸' },
	{ code: 'nb', name: 'Norsk', flag: '🇳🇴' }
].filter(lang => tenant.features.allowedLocales.includes(lang.code as Locales))

const Header = () => {
	const { L, setLocale, locale } = useI18n()
	const routerState = useRouterState()
	const { isLoggedIn, customer } = useAuth()
	const logOut = useLogOut()
	const deleteAccount = useBaseDeleteAccount(true)
	const [logoutDialogOpen, setLogoutDialogOpen] = useState(false)
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
	const [deleteFinalConfirmOpen, setDeleteFinalConfirmOpen] = useState(false)

	const { features } = tenant

	const currentCart = useGetCurrentOrder({ enabled: features.ecomEnabled })
	const listCategories = useGetCategories({}, { enabled: features.ecomEnabled })

	const categoryUuid = routerState.location.search?.category
	const showCategories =
		features.ecomEnabled &&
		(routerState.location.pathname === '/' || /^\/product\/[^/]+$/.test(routerState.location.pathname))

	const itemCount =
		currentCart.data?.itemGroups
			?.flatMap(x => x.items)
			?.reduce((acc, item) => acc + (item.quantityInfo.quantity ?? 0), 0) ?? 0

	const currentLanguage = languages.find(lang => lang.code === locale)

	const handleLanguageChange = (langCode: Locales) => {
		setLocale(langCode)
	}

	const showLogout = hasAccountLogin() && isLoggedIn
	const showDeleteAccount = hasAccountLogin() && isLoggedIn

	const handleDeleteAccount = () => {
		deleteAccount.mutate(
			{},
			{
				onSuccess: result => {
					if (result.isSuccess) {
						setDeleteFinalConfirmOpen(false)
						logOut()
					} else {
						toast.error(L.Errors.Generic())
					}
				},
				onError: () => toast.error(L.Errors.Generic())
			}
		)
	}

	return (
		<header className='bg-background/95 supports-[backdrop-filter]:bg-background/60 sticky top-0 z-50 flex w-full border-b backdrop-blur'>
			<div className='w-full flex-1'>
				{/* Main header row */}
				<div className='mx-auto flex h-16 max-w-6xl items-center justify-between px-2 sm:px-4'>
					<Link to='/' className='transition-opacity hover:opacity-80'>
						<TenantLogo
							asset={tenant.logoLarge}
							alt={features.name}
							className='text-primary h-8 w-auto sm:h-10'
							fallback={<span className='text-primary text-xl font-bold'>{features.name}</span>}
						/>
					</Link>

					<div className='flex items-center space-x-1 sm:space-x-2'>
						{/* Loyalty Card Link */}
						{features.loyaltyCardEnabled && isLoggedIn && (
							<Button asChild variant='ghost' size='icon' className='group h-10 w-10'>
								<Link to='/loyalty' aria-label={L.LoyaltyCard.Title()}>
									<CreditCard className='group-hover:text-primary h-5 w-5 transition-colors' />
								</Link>
							</Button>
						)}

						{/* Receipts Link */}
						<Button asChild variant='ghost' size='icon' className='group h-10 w-10'>
							<Link
								to='/receipts'
								className='flex items-center justify-center'
								aria-label={L.Receipts.Title()}
							>
								<ReceiptText className='group-hover:text-primary h-5 w-5 transition-colors' />
							</Link>
						</Button>

						{/* Cart (ecom only) */}
						{features.ecomEnabled && (
							<HoverCard openDelay={10}>
								<HoverCardTrigger asChild>
									<Button asChild variant='ghost' size='icon' className='group relative h-10 w-10'>
										<Link
											to='/cart'
											className='flex items-center justify-center'
											aria-label={L.Cart.Title()}
										>
											<ShoppingCart className='group-hover:animate-wiggle h-5 w-5 transition-transform' />
											{itemCount > 0 && (
												<span className='bg-primary text-primary-foreground animate-in zoom-in-50 absolute -top-1 -right-1 flex h-5 w-5 items-center justify-center rounded-full text-xs duration-300'>
													<span
														key={itemCount}
														className='animate-in zoom-in-50 duration-300'
													>
														{itemCount}
													</span>
												</span>
											)}
										</Link>
									</Button>
								</HoverCardTrigger>
								<HoverCardContent align='end' className='w-80'>
									{!currentCart.data?.itemGroups?.flatMap(x => x.items).length ?
										<div className='py-6 text-center'>
											<p className='text-muted-foreground text-sm'>{L.Cart.Empty()}</p>
										</div>
									:	<div className='space-y-4'>
											<ScrollArea className='max-h-80'>
												<div className='space-y-3'>
													{currentCart.data?.itemGroups
														?.flatMap(x => x.items)
														?.map(item => {
															return (
																<div
																	key={item.uuid}
																	className='flex items-center gap-4'
																>
																	<Image
																		src={item.variantImageUrl}
																		alt={item.productTitle}
																		className='bg-secondary/10 h-12 w-12 rounded-md object-contain p-1'
																	/>
																	<div className='flex-1 space-y-1'>
																		<p className='text-sm leading-none font-medium'>
																			{item.productTitle}
																		</p>
																		<p className='text-muted-foreground text-sm'>
																			{L.Amount.QuantityTimesPrice(
																				item.quantityInfo.quantity,
																				{
																					currencyCode:
																						currentCart.data?.currencyCode,
																					amount: item.unitPrice?.gross
																				}
																			)}
																		</p>
																	</div>
																</div>
															)
														})}
												</div>
											</ScrollArea>
											<div className='border-t pt-4'>
												<div className='flex justify-between'>
													<p className='text-sm font-medium'>{L.Cart.Total()}</p>
													<p className='text-sm font-bold'>
														{L.Amount.Default({
															currencyCode: currentCart.data?.currencyCode,
															amount: currentCart.data?.totals.gross
														})}
													</p>
												</div>
												<Button asChild className='mt-4 w-full' size='sm'>
													<Link to='/cart'>{L.Cart.ViewCart()}</Link>
												</Button>
											</div>
										</div>
									}
								</HoverCardContent>
							</HoverCard>
						)}

						{/* Language menu */}
						{languages.length > 1 && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button
										variant='ghost'
										size='icon'
										className='h-10 w-10'
										aria-label={L.Profile.Language()}
									>
										{currentLanguage ?
											<span className='text-sm font-medium'>{currentLanguage.flag}</span>
										:	<Globe className='h-5 w-5' />}
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end' className='min-w-[160px]'>
									<DropdownMenuLabel>{L.Profile.Language()}</DropdownMenuLabel>
									{languages.map(language => (
										<DropdownMenuItem
											key={language.code}
											onClick={() => handleLanguageChange(language.code as Locales)}
											className='flex items-center gap-2'
										>
											<span>{language.flag}</span>
											<span>{language.name}</span>
											{locale === language.code && <Check className='ml-auto h-3 w-3' />}
										</DropdownMenuItem>
									))}
								</DropdownMenuContent>
							</DropdownMenu>
						)}

						{/* Profile menu: account actions */}
						{(showLogout || showDeleteAccount) && (
							<DropdownMenu>
								<DropdownMenuTrigger asChild>
									<Button variant='ghost' size='icon' className='h-10 w-10' aria-label='Account'>
										<User className='h-5 w-5' />
									</Button>
								</DropdownMenuTrigger>
								<DropdownMenuContent align='end' className='min-w-[160px]'>
									{showDeleteAccount && (
										<DropdownMenuItem
											onClick={() => setDeleteConfirmOpen(true)}
											className='text-destructive focus:text-destructive'
										>
											<Trash2 className='mr-2 h-4 w-4' />
											{L.Profile.DeleteAccount()}
										</DropdownMenuItem>
									)}
									{showLogout && (
										<>
											{showDeleteAccount && <DropdownMenuSeparator />}
											<DropdownMenuItem
												onClick={() => setLogoutDialogOpen(true)}
												className='text-destructive focus:text-destructive'
											>
												<LogOut className='mr-2 h-4 w-4' />
												{L.Profile.Logout()}
											</DropdownMenuItem>
										</>
									)}
								</DropdownMenuContent>
							</DropdownMenu>
						)}
					</div>
				</div>

				{/* Category Navigation - only shown on product list page */}
				{showCategories && (
					<div className='border-border/40 bg-muted/30 border-t'>
						<ScrollArea className='w-full whitespace-nowrap'>
							<div className='flex justify-center'>
								<div className='flex w-max space-x-1 px-2 py-2 sm:space-x-2 sm:px-4 sm:py-3'>
									<Link
										to='/'
										search={{ category: undefined }}
										className={`rounded-md px-2 py-1 text-xs whitespace-nowrap transition-all sm:px-3 sm:text-sm ${
											!categoryUuid ?
												'bg-primary text-primary-foreground hover:bg-primary/80 font-medium'
											:	'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
										}`}
									>
										{L.Product.Catalog.All()}
									</Link>
									{listCategories.data?.map(category => (
										<Link
											key={category.uuid}
											to='/'
											search={{ category: category.uuid }}
											className={`rounded-md px-2 py-1 text-xs whitespace-nowrap transition-all sm:px-3 sm:text-sm ${
												categoryUuid === category.uuid ?
													'bg-primary text-primary-foreground hover:bg-primary/80 font-medium'
												:	'text-muted-foreground hover:bg-primary/10 hover:text-foreground'
											}`}
										>
											{category.name}
										</Link>
									))}
								</div>
							</div>
							<ScrollBar orientation='horizontal' />
						</ScrollArea>
					</div>
				)}
			</div>

			{/* Logout confirmation dialog */}
			<Dialog open={logoutDialogOpen} onOpenChange={setLogoutDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>{L.Profile.LogoutConfirm()}</DialogTitle>
						<DialogDescription>{L.Profile.LogoutConfirmMessage()}</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button variant='outline' onClick={() => setLogoutDialogOpen(false)}>
							{L.Common.Actions.Cancel()}
						</Button>
						<Button variant='destructive' onClick={logOut}>
							{L.Profile.Logout()}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			<AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{L.Profile.DeleteAccountConfirm()}</AlertDialogTitle>
						<AlertDialogDescription>{L.Profile.DeleteAccountConfirmMessage()}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{L.Common.Actions.Cancel()}</AlertDialogCancel>
						<AlertDialogAction
							className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
							onClick={() => {
								setDeleteConfirmOpen(false)
								setDeleteFinalConfirmOpen(true)
							}}
						>
							{L.Profile.DeleteAccountConfirmButton()}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>

			<AlertDialog open={deleteFinalConfirmOpen} onOpenChange={setDeleteFinalConfirmOpen}>
				<AlertDialogContent>
					<AlertDialogHeader>
						<AlertDialogTitle>{L.Profile.DeleteAccountFinalConfirm()}</AlertDialogTitle>
						<AlertDialogDescription>{L.Profile.DeleteAccountFinalConfirmMessage()}</AlertDialogDescription>
					</AlertDialogHeader>
					<AlertDialogFooter>
						<AlertDialogCancel>{L.Common.Actions.Cancel()}</AlertDialogCancel>
						<AlertDialogAction
							className='bg-destructive text-destructive-foreground hover:bg-destructive/90'
							onClick={handleDeleteAccount}
							disabled={deleteAccount.isPending}
						>
							{L.Profile.DeleteAccountConfirmButton()}
						</AlertDialogAction>
					</AlertDialogFooter>
				</AlertDialogContent>
			</AlertDialog>
		</header>
	)
}

export default Header
